/**
 * 25 - Forward Observer (10 §2): a wired park of things worth an air strike - fuel, ammunition, a launcher -
 * and three observation points among the rocks outside the wire, each looking in at it. Eight seconds
 * unseen at a point marks its target; being seen there throws that mark away. When the third is marked the
 * strike is on its way, and lands on all three targets: be 60 m clear of every one of them.
 *
 * How long the strike takes is set by `finish` from the map: long enough to WALK clear from whichever point
 * happens to be marked last. Engine limit: caught inside the 60 m he is cut down (three wounds), not vaporised.
 */

import { RANGER } from '../../sim/tuning';
import type { PropDef, PropKind, Rect } from '../../sim/types';
import type { Point } from '../types';
import { checker, clutter, guardAtDoor, guardsAbout, insideRect, required, structure } from './helpers';
import { clearing, flagTargets, hideRadius, openNorth, RECALL, rockHide } from './helpers3b';
import { timedRule } from './helpers3bTime';
import type { ArchetypeSpec } from './types';

const POINTS = 3;
const DWELL_SECONDS = 8;
const CLEAR_DISTANCE = 60;
const STRIKE_KINDS: readonly PropKind[] = ['fuelTank', 'ammoShack', 'mobileLauncher'];
/**
 * Points stand at least this far apart (two hides' rocks never touch: a hide is 6.8 m across its radius; they are
 * PLACED as far apart as the ground allows), this far outside the wire, and no farther than this from something
 * they mark - inside the 60 m the strike wants clear, so marking always leaves him a walk to safety, never a sprint.
 */
const APART = 20;
const OUTSIDE = hideRadius + 1.5;
const OVERLOOKS = 58;
const LYING_ROOM = 12;

const gap = (a: Point, b: Point): number => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));
const middle = (r: Rect): Point => ({ x: (r.x0 + r.x1) / 2, z: (r.z0 + r.z1) / 2 });

/** Seconds to walk 60 m clear of every target from the worst-placed point, half as long again, and ten to react. */
export function strikeSeconds(points: readonly Point[], targets: readonly Point[]): number {
  let worst = 0;
  for (const p of points) worst = Math.max(worst, CLEAR_DISTANCE + 5 - Math.min(...targets.map((t) => gap(p, t))));
  return Math.max(20, Math.ceil(((worst / RANGER.walkSpeed) * 1.5 + 10) / 5) * 5);
}

export const forwardObserver: ArchetypeSpec = {
  id: 'forward-observer',
  name: 'FORWARD OBSERVER',
  nativeBiome: 'desert',
  baseThreat: 5,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wire',
  sealed: false,
  layout: 'compound',
  size: { w: [52, 60], d: [28, 32] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    // What the strike is for. Not flagged: the ranger's business is with the three points outside the wire.
    const placed: Point[] = [];
    const struck: PropDef[] = STRIKE_KINDS.map((kind) => required(g, kind, { minDist: 11, from: placed, margin: 1.6 }));
    const house = structure(g, 'guardHouse', { minDist: 9, from: placed, margin: 1.2 });
    structure(g, g.kit.shelter, { minDist: 8, from: placed, margin: 1.2 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    if (house) guardAtDoor(g, house);
    guardsAbout(g, 2 + Math.floor(g.threat / 3));

    // The points: outside the wire, apart, each with its back-stop of rocks toward the target it marks.
    const from: Point[] = [];
    const clearOf = [{ at: g.ctx.plan.pickup, radius: 14 }];
    if (g.ctx.quiet.end) clearOf.push({ at: g.ctx.quiet.end, radius: 12 });
    const zones: Rect[] = [];
    const markers: PropDef[] = [];
    const left = [...struck];
    const near = middle(g.rect);
    const overlooks = (p: Point): boolean => struck.some((t) => gap(p, t) <= OVERLOOKS);
    for (let i = 0; i < POINTS; i++) {
      const at = clearing(g, {
        within: openNorth(g), radius: hideRadius, minDist: APART, from, clearOf, spread: true,
        outside: { rect: g.rect, pad: OUTSIDE }, accept: overlooks,
      });
      if (!at) {
        g.map.problems.push('forward-observer: no room for three observation points round the wire');
        break;
      }
      // Each point marks the target nearest it that no other point has.
      left.sort((a, b) => gap(a, at) - gap(b, at));
      const target = left.shift() ?? struck[0] ?? near;
      zones.push(rockHide(g, at, target));
      markers.push(g.map.add('observationPost', at.x, at.z));
    }
    const ids = markers.map((m) => m.id);
    const strikePropIds = struck.map((p) => p.id);
    return {
      objective: {
        kind: 'visitZones', label: 'MARK THE TARGETS', targetPropIds: ids, minFraction: 1,
        zones, dwellSeconds: DWELL_SECONDS, dwellUnseen: true, clearDistance: CLEAR_DISTANCE,
        rules: [
          {
            type: 'timedEvent', atSeconds: 40, effect: 'strikeLands', from: 'complete', strikePropIds,
            orders: `HOLD EACH POINT ${DWELL_SECONDS} SECONDS UNSEEN. THE STRIKE FOLLOWS THE THIRD MARK: BE ${CLEAR_DISTANCE} M CLEAR OF THE COMPOUND`,
            warning: 'THE STRIKE LANDS IN',
            reason: 'CAUGHT IN HIS OWN STRIKE',
          },
          RECALL,
        ],
      },
      finish(def) {
        flagTargets(def, ids);
        const rule = timedRule(def, 'strikeLands');
        if (rule) rule.atSeconds = strikeSeconds(zones.map(middle), struck);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('forward-observer');
    const o = def.objective;
    const zones = o.zones ?? [];
    check(o.kind === 'visitZones' && o.dwellSeconds === DWELL_SECONDS && o.dwellUnseen === true, 'three points, 8 s each, unseen');
    check(zones.length === POINTS && o.targetPropIds.length === POINTS, `needs ${POINTS} observation points, each with its marker`);
    check(o.clearDistance === CLEAR_DISTANCE, `the strike must want ${CLEAR_DISTANCE} m of clearance`);
    zones.forEach((zone, i) => {
      const marker = def.props.find((p) => p.id === o.targetPropIds[i]);
      check(marker?.kind === 'observationPost' && marker.target === true, `point ${i + 1} must be marked on the strip map`);
      check(marker !== undefined && insideRect(zone, marker.x, marker.z), `point ${i + 1}'s marker must stand in its zone`);
      check(!insideRect(def.goalZone, middle(zone).x, middle(zone).z), `point ${i + 1} must lie outside the wire`);
      check(probe.standable(zone) >= LYING_ROOM, `point ${i + 1} must have room to lie in`);
      if (marker) check(probe.breach(marker.id) === 0, `point ${i + 1} must be reachable without crossing mines or wire`);
      for (let j = 0; j < i; j++) {
        const other = zones[j];
        if (other) check(gap(middle(zone), middle(other)) >= APART - 0.5, `points ${j + 1} and ${i + 1} are too close together`);
      }
    });
    const rule = timedRule(def, 'strikeLands');
    const struck = def.props.filter((p) => rule?.strikePropIds?.includes(p.id));
    check(rule?.from === 'complete', 'the strike must follow the last mark');
    check(struck.length === STRIKE_KINDS.length, `the strike needs ${STRIKE_KINDS.length} targets`);
    check(struck.every((p) => insideRect(def.goalZone, p.x, p.z) && p.target !== true), 'what is struck stands inside the wire, unflagged');
    if (struck.length > 0 && zones.length > 0) {
      check(zones.every((z) => Math.min(...struck.map((t) => gap(middle(z), t))) <= OVERLOOKS + 1), 'every point must overlook the compound');
      check((rule?.atSeconds ?? 0) >= strikeSeconds(zones.map(middle), struck), 'the strike must leave time to walk clear from any point');
    }
    check(
      rule?.orders !== undefined && rule.warning !== undefined && rule.reason !== undefined,
      'the strike must carry its orders, its countdown and its reason',
    );
    return out;
  },
};
